export type JobPreviewStatus = {
  id: string;
  state: string;
  error: string | null;
  preview:
    | {
        ready: true;
        url: string;
        storage: string | null;
        measurements: Record<string, unknown> | null;
        contentHash: string | null;
        rendererVersion: string | null;
        outputBytes: number | null;
      }
    | { ready: false };
};

export type PollJobPreviewOptions = {
  jobId: string;
  signal?: AbortSignal;
  intervalMs?: number;
  timeoutMs?: number;
  onTick?: (status: JobPreviewStatus) => void;
};

/** Poll `/api/jobs/:id` until preview ready, failed, or timeout. */
export async function pollJobPreview(
  options: PollJobPreviewOptions,
): Promise<JobPreviewStatus> {
  const intervalMs = options.intervalMs ?? 1000;
  const timeoutMs = options.timeoutMs ?? 90_000;
  const started = Date.now();

  while (true) {
    if (options.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    if (Date.now() - started > timeoutMs) {
      throw new Error("Preview render timed out");
    }

    const res = await fetch(`/api/jobs/${options.jobId}`, {
      credentials: "include",
      signal: options.signal,
    });
    if (!res.ok) {
      throw new Error(`Job status failed (${res.status})`);
    }
    const status = (await res.json()) as JobPreviewStatus;
    options.onTick?.(status);

    if (status.preview.ready) return status;
    if (status.state === "failed") {
      throw new Error(status.error ?? "Preview render failed");
    }

    await sleep(intervalMs, options.signal);
  }
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
